import React, { useState, useEffect, useMemo, useRef } from 'react'
import './MainHub.css'
import { useAuth } from '../../Auth';
import '../../client-dashboard/clientDashboardMap.css';
import FilterDropdown from "../TeamPortalUtil/FilterDropdown";
import { useLocation } from "react-router-dom";
import { useDetectorRelations } from '../HardwareTab/useDetectorRelations';

// Leaflet imports
import {
    MapContainer,
    TileLayer,
    Marker,
    Popup,
    useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "react-leaflet-markercluster/dist/styles.min.css";
import { Icon, Map as LeafletMap } from "leaflet";
import { GeoSearchControl } from 'leaflet-geosearch';
import 'leaflet-geosearch/dist/geosearch.css';

// Assets
import fnnPopupLogo from '../../img/FNNLogoWithBlackFont-06.png'
import greenMarker from '../../client-dashboard/client-dashboard-assets/mapdetectoricon-blue.png'
import offlineMarker from '../../client-dashboard/client-dashboard-assets/mapdetectoricon-grey.png'
import googleMapsLogo from '../../backend/assets/google-map-logo.png'

// Icons
import { FaChevronDown, FaCircle } from "react-icons/fa";

// Define the Detector type for TypeScript
interface DetectorObject {
    mac: string;
    ip: string;
    last_active: string;
    online: boolean;
    status: {
        site_name: string;
        gps_fix: boolean;
        gps_lat: number;
        gps_long: number;
        up_since: string;
        memory: string;
        fw_version: string;
        test_mode: boolean;
        thr: string;
        linux: boolean;
        sim_card_no?: string;
    };
}

// Pseudo-region key for detectors that are not in any region group
const UNASSIGNED = "unassigned";

// --- Local Development Mode ---
// Set to true to send API calls to localhost:8000
const LOCAL_MODE = false;

function apiUrl(path: string): string {
    return LOCAL_MODE ? `http://localhost:8000${path}` : path;
}

function credentials(): RequestCredentials {
    return LOCAL_MODE ? "include" : "same-origin";
}

// Turns a DRF error response into one readable sentence
function getErrorMessage(data: any, status: number): string {
    if (data?.already_assigned) {
        const macs = Object.keys(data.already_assigned);
        return `${macs.join(", ")} ${macs.length === 1 ? "is" : "are"} already in a region. Refresh the page and try again.`;
    }
    if (data?.error) return data.error;
    if (data?.detail) return data.detail;

    const first = data && typeof data === "object" ? Object.values(data)[0] : null;
    if (Array.isArray(first) && first.length) return String(first[0]);

    return `The request failed (HTTP ${status}).`;
}

// Search cities and addresses logic
interface Result {
    lon: string;
    lat: string;
    display_name: string;
    boundingbox: string[];
}

const DjangoNominatimProvider = {
    search: (() => {
        let timeout: NodeJS.Timeout | null = null;
        let controller: AbortController | null = null;

        return ({ query }: { query: string }) => {
            if (!query) return Promise.resolve([]);

            if (controller) controller.abort();
            if (timeout) clearTimeout(timeout);

            return new Promise((resolve, reject) => {
                timeout = setTimeout(() => {
                    controller = new AbortController();

                    fetch(`/api/geocode?q=${encodeURIComponent(query)}`, {
                        signal: controller.signal,
                    })
                        .then((response) => response.json())
                        .then((results: Result[]) => {
                            resolve(
                                results.map((r) => ({
                                    x: parseFloat(r.lon),
                                    y: parseFloat(r.lat),
                                    label: r.display_name,
                                    bounds: [
                                        [parseFloat(r.boundingbox[0]), parseFloat(r.boundingbox[2])],
                                        [parseFloat(r.boundingbox[1]), parseFloat(r.boundingbox[3])],
                                    ],
                                    raw: r,
                                }))
                            );
                        })
                        .catch((e) => {
                            if ((e as any).name === "AbortError") return;
                            reject(e);
                        });
                }, 200);
            });
        };
    })(),
};

const SearchControl: React.FC = () => {
    const map = useMap();

    useEffect(() => {
        const searchControl = new (GeoSearchControl as any)({
            provider: DjangoNominatimProvider,
            style: "bar",
            showMarker: false,
            showPopup: true,
            popupFormat: ({ query, result }: { query: string; result: { label: string } }) =>
                `You searched for: ${query}<br>Result: ${result.label}`,
            searchLabel: "Search Address or Zip",
        });

        map.addControl(searchControl);

        return () => {
            map.removeControl(searchControl);
        };
    }, [map]);

    return null;
};

// Leaflet measures its container once; tell it to re-measure after entering/leaving fullscreen
const MapResizer = ({ trigger }: { trigger: boolean }) => {
    const map = useMap();

    useEffect(() => {
        const id = setTimeout(() => {
            if (map && map.getContainer()) map.invalidateSize();
        }, 50);
        return () => clearTimeout(id);
    }, [trigger, map]);

    return null;
};

export const BwIcon = new Icon({
    iconUrl: offlineMarker,
    iconSize: [50, 50]
});

export const greenIcon = new Icon({
    iconUrl: greenMarker,
    iconSize: [50, 50]
});

const getColorForGPSFix = (isOn: boolean) => {
    if (isOn === false) return "red";
    if (isOn === true) return "#008000";
};

const getColorForMarker = (isOn: boolean, memoryValue: string | number) => {
    if (isOn === false) return BwIcon;
    if (isOn === true) return greenIcon;
};

interface MainHubProps {
    onRegionChange?: (regions: string[]) => void;
}

const MainHub: React.FC<MainHubProps> = ({ onRegionChange }) => {

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const { getDashboardPermissions } = useAuth();
    const { detectorCodeObj, detectorGroups, unassignedDetectors, refreshDetectors } = useDetectorRelations();

    // Pre-compute latestCodes the same way useDetectorRelations does
    const latestCodes = useMemo(() => {
        const safe = Array.isArray(detectorCodeObj) ? detectorCodeObj : [];
        return Object.values(
            safe.reduce((acc, c) => {
                const key = c.receiver?.toLowerCase().replace(/^(0x|000)/, "") || "";
                acc[key] = c;
                return acc;
            }, {} as Record<string, typeof detectorCodeObj[0]>)
        );
    }, [detectorCodeObj]);

    interface EnrichedDetector extends DetectorObject {
        code?: string;
        codeName?: string;
        codeId?: string;
        codeEntry?: typeof detectorCodeObj[0];
    }

    const enrichDetector = (det: DetectorObject): EnrichedDetector => {
        const normalizedMac = det.mac?.toLowerCase().replace(/^(0x|000)/, "") || "";
        const codeEntry = latestCodes.find(
            (c) => (c.receiver?.toLowerCase().replace(/^(0x|000)/, "") || "") === normalizedMac
        );
        return {
            ...det,
            code: codeEntry?.code,
            codeName: codeEntry?.name,
            codeId: codeEntry?.id,
            codeEntry,
        };
    };

    const formatDetectorName = (name: string | undefined, fallback: string): string => {
        if (!name) return fallback;

        return name
            .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
            .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
            .trim();
    };

    const [regions, setRegions] = useState<string[]>([]);
    const location = useLocation();
    const preselectedRegion = location.state?.preselectedRegion || "All";
    const [selectedRegions, setSelectedRegions] = useState<string[]>([preselectedRegion]);

    const [statusFilter, setStatusFilter] = useState<"all" | "online" | "offline">("all");
    const [expandedRegions, setExpandedRegions] = useState<{ [region: string]: boolean }>({});

    const formatRegionName = (name: string) =>
        name.replace(/^detectors_/, '')
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

    const handleFilterChange = (filters: { [key: string]: string[] }) => {
        const selected = filters.regions || [];
        if (selected.length === 0) {
            setSelectedRegions(["All"]);
            onRegionChange?.(["All"]);
        } else {
            setSelectedRegions(selected);
            onRegionChange?.(selected);
        }
    };

    // regional detectors (per region)
    const [detectorDataRegionalized, setDetectorDataRegionalized] = useState<{ [region: string]: DetectorObject[] }>({});

    const mapRef = useRef<LeafletMap | null>(null);

    const [isFullscreen, setIsFullscreen] = useState(false);

    useEffect(() => {
        if (!isFullscreen) return;

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") setIsFullscreen(false);
        };
        const previousOverflow = document.body.style.overflow;

        document.addEventListener("keydown", onKeyDown);
        document.body.style.overflow = "hidden";

        return () => {
            document.removeEventListener("keydown", onKeyDown);
            document.body.style.overflow = previousOverflow;
        };
    }, [isFullscreen]);

    // Countries each region belongs to (from the hook's /api/detectordashapi/ data)
    const regionCountries = useMemo(() => {
        const mapping: Record<string, string> = {};
        detectorGroups.forEach(g => {
            if (g.Belongs_to) mapping[g.GROUP_NAME] = g.Belongs_to;
        });
        return mapping;
    }, [detectorGroups]);

    // Read inside the fly-to effect, so refreshing groups doesn't move the map
    const regionCountriesRef = useRef(regionCountries);
    regionCountriesRef.current = regionCountries;

    // Fetch one region's detectors (used on load and after adding detectors to a region)
    const fetchRegionDetectors = async (regionKey: string): Promise<DetectorObject[]> => {
        try {
            const res = await fetch(apiUrl(`/apidet/${regionKey}/`), { credentials: credentials() });
            if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
            const data = await res.json();

            if (Array.isArray(data)) return data;
            if (data?.Message) console.warn(`No detectors in region ${regionKey}:`, data.Message);
            return [];
        } catch (err) {
            console.error(`Error fetching detectors for region ${regionKey}:`, err);
            return [];
        }
    };

    // Set before a background data update so the map doesn't fly away from where the user is
    const skipNextFlyRef = useRef(false);

    // Fetch regional detectors
    useEffect(() => {
        const fetchRegions = async () => {
            try {
                const groups = await getDashboardPermissions();
                const detectorRegions = Object.keys(groups).filter(key => key.startsWith('detectors_'));

                setRegions(["All", ...detectorRegions]);

                setSelectedRegions(prev => {
                    if (prev.length > 0 && regions.includes(prev[0])) {
                        return prev;
                    }
                    if (preselectedRegion && ["All", UNASSIGNED, ...detectorRegions].includes(preselectedRegion)) {
                        return [preselectedRegion];
                    }
                    return ["All"];
                });

                const entries = await Promise.all(
                    detectorRegions.map(async (regionKey) =>
                        [regionKey, await fetchRegionDetectors(regionKey)] as const
                    )
                );

                setDetectorDataRegionalized(Object.fromEntries(entries));
            } catch (err) {
                console.error("Error fetching dashboard permissions:", err);
                setRegions([]);
                setDetectorDataRegionalized({});
            }
        };

        fetchRegions();
    }, [getDashboardPermissions]);

    // Show/hide the Unassigned pseudo-region (remembered per browser)
    const [showUnassigned, setShowUnassigned] = useState<boolean>(() => {
        try {
            return localStorage.getItem("mainHub.showUnassigned") !== "false";
        } catch {
            return true;
        }
    });

    const toggleShowUnassigned = () => {
        const next = !showUnassigned;
        setShowUnassigned(next);
        try {
            localStorage.setItem("mainHub.showUnassigned", String(next));
        } catch {
            /* storage unavailable: preference just won't persist */
        }

        if (!next) {
            // Leave assign mode and drop Unassigned from the current selection
            setAssignMode(false);
            setSelectedMacs([]);
            setTargetRegion("");
            setSelectedRegions(prev => {
                const remaining = prev.filter(r => r !== UNASSIGNED);
                return remaining.length > 0 ? remaining : ["All"];
            });
        }
    };

    // Real regions + the Unassigned pseudo-region (only when shown and it has detectors)
    const regionData = useMemo(() => {
        const data: { [region: string]: DetectorObject[] } = { ...detectorDataRegionalized };
        if (showUnassigned && unassignedDetectors.length > 0) {
            data[UNASSIGNED] = unassignedDetectors.map(d => d.monitoring);
        }
        return data;
    }, [detectorDataRegionalized, unassignedDetectors, showUnassigned]);

    const hasUnassigned = UNASSIGNED in regionData;

    // Kept in a ref so the 5s monitoring poll doesn't re-trigger the map fly-to
    const unassignedRef = useRef(unassignedDetectors);
    unassignedRef.current = unassignedDetectors;

    // FilterDropdown options: real regions, then Unassigned last
    const regionFilterOptions = [
        ...regions.filter(r => r !== "All"),
        ...(hasUnassigned ? [UNASSIGNED] : []),
    ];

    // Determine selected detectors ignoring duplicates
    const selectedDetectors = useMemo(() => {
        if (selectedRegions.length === 0) return [];

        const combined: DetectorObject[] = selectedRegions.includes("All")
            ? Object.values(regionData).flat()
            : selectedRegions.flatMap(region => regionData[region] || []);

        const deduped = Array.from(new Map(combined.map(det => [det.mac, det])).values());
        return deduped.map(enrichDetector);
    }, [selectedRegions, regionData, latestCodes]);

    const filteredDetectors = useMemo(() => {
        if (statusFilter === "online") return selectedDetectors.filter(det => det.online);
        if (statusFilter === "offline") return selectedDetectors.filter(det => !det.online);
        return selectedDetectors;
    }, [selectedDetectors, statusFilter]);

    const onlineCount = selectedDetectors.filter(det => det.online).length;
    const offlineCount = selectedDetectors.length - onlineCount;

    const markerRefs = useRef<{ [key: string]: any }>({});
    const openPopup = (mac: string) => {
        const marker = markerRefs.current[mac];
        if (marker) {
            marker.openPopup();
            const { lat, lng } = marker.getLatLng();
            const offsetLat = lat + 1.5;
            mapRef.current?.flyTo([offsetLat, lng], 7, { duration: 1.2 });
        }
    };

    // ------------------------------------ Add unassigned detectors to a region ------------------------------------
    // Only Detector Team members get can_assign = true from the backend
    const [assignAccess, setAssignAccess] = useState<{ canAssign: boolean; regions: string[] }>({
        canAssign: false,
        regions: [],
    });
    const [assignMode, setAssignMode] = useState(false);
    const [selectedMacs, setSelectedMacs] = useState<string[]>([]);
    const [targetRegion, setTargetRegion] = useState("");
    const [assigning, setAssigning] = useState(false);
    const [assignMessage, setAssignMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

    useEffect(() => {
        const fetchAssignAccess = async () => {
            try {
                const res = await fetch(apiUrl("/api/detector-groups/assign/"), { credentials: credentials() });
                if (!res.ok) return; // 403 for non-FNN users: keep the feature hidden
                const data = await res.json();
                setAssignAccess({
                    canAssign: !!data.can_assign,
                    regions: Array.isArray(data.regions) ? data.regions : [],
                });
            } catch (err) {
                console.error("Error checking detector assignment access:", err);
            }
        };

        fetchAssignAccess();
    }, []);

    // Drop selections for detectors that stopped being unassigned (e.g. someone else assigned them)
    const unassignedMacs = useMemo(
        () => new Set(unassignedDetectors.map(d => d.monitoring.mac)),
        [unassignedDetectors]
    );
    const validSelectedMacs = selectedMacs.filter(mac => unassignedMacs.has(mac));

    const toggleSelectedMac = (mac: string) => {
        setSelectedMacs(prev => (prev.includes(mac) ? prev.filter(m => m !== mac) : [...prev, mac]));
    };

    const exitAssignMode = () => {
        setAssignMode(false);
        setSelectedMacs([]);
        setTargetRegion("");
    };

    const handleAssign = async () => {
        if (!targetRegion || validSelectedMacs.length === 0) return;

        setAssigning(true);
        setAssignMessage(null);

        try {
            const csrfRes = await fetch(apiUrl("/api/csrf-token/"), { credentials: credentials() });
            if (!csrfRes.ok) throw new Error("Could not get a CSRF token. Refresh the page and try again.");
            const { csrfToken } = await csrfRes.json();

            const res = await fetch(apiUrl("/api/detector-groups/assign/"), {
                method: "POST",
                credentials: credentials(),
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": csrfToken,
                },
                body: JSON.stringify({ group_name: targetRegion, macs: validSelectedMacs }),
            });

            const data = await res.json().catch(() => null);
            if (!res.ok) throw new Error(getErrorMessage(data, res.status));

            const count = validSelectedMacs.length;
            const regionLabel = formatRegionName(targetRegion);
            exitAssignMode();
            setAssignMessage({
                type: "success",
                text: `Added ${count} detector${count === 1 ? "" : "s"} to ${regionLabel}.`,
            });

            // Refresh groups (removes them from Unassigned) and the target region, if this user can see it
            await Promise.all([
                refreshDetectors(),
                regions.includes(targetRegion)
                    ? fetchRegionDetectors(targetRegion).then(dets => {
                        skipNextFlyRef.current = true;
                        setDetectorDataRegionalized(prev => ({ ...prev, [targetRegion]: dets }));
                    })
                    : Promise.resolve(),
            ]);
        } catch (err: any) {
            setAssignMessage({ type: "error", text: err?.message || "Could not add the detectors. Try again." });
        } finally {
            setAssigning(false);
        }
    };

    const renderDetectorButton = (detector: EnrichedDetector, selectable = false) => {
        let displayId = detector.codeId;
        let displayCode = detector.code;

        if (detector.codeId && !detector.code) {
            displayCode = "U";
        } else if (!detector.codeId && !detector.code) {
            displayId = "-";
            displayCode = "T";
        }

        const isSelected = selectable && selectedMacs.includes(detector.mac);

        return (
            <button
                key={detector.mac}
                type="button"
                className={`mh-det-chip ${detector.online ? "online" : "offline"} ${selectable ? "selectable" : ""} ${isSelected ? "selected" : ""}`}
                title={formatDetectorName(detector.codeName, detector.status.site_name || detector.mac)}
                aria-pressed={selectable ? isSelected : undefined}
                onClick={() => (selectable ? toggleSelectedMac(detector.mac) : openPopup(detector.mac))}
            >
                <FaCircle aria-hidden="true" />
                {displayId} {displayCode}
            </button>
        );
    };

    function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
        const R = 6371;
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) *
            Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    }

    useEffect(() => {
        const map = mapRef.current;
        if (!map || selectedRegions.length === 0) return;

        // A region was refreshed in the background: leave the map where the user put it
        if (skipNextFlyRef.current) {
            skipNextFlyRef.current = false;
            return;
        }

        if (selectedRegions.includes("All")) {
            map.flyTo([0.0, 0.0], 2);
            return;
        }

        const realRegions = selectedRegions.filter(r => r !== UNASSIGNED);

        // Only Unassigned selected: those detectors are scattered, so fit them all
        if (realRegions.length === 0) {
            const points = unassignedRef.current
                .map(d => [Number(d.monitoring.status.gps_lat), Number(d.monitoring.status.gps_long)] as [number, number])
                .filter(([lat, lng]) => lat && lng);

            if (points.length > 0) {
                map.flyToBounds(points, { padding: [40, 40], maxZoom: 7 });
            } else {
                map.flyTo([0.0, 0.0], 2);
            }
            return;
        }

        if (realRegions.length === 1) {
            const detectors = detectorDataRegionalized[realRegions[0]] || [];
            if (detectors.length > 0) {
                const lat = Number(detectors[0].status.gps_lat);
                const lng = Number(detectors[0].status.gps_long);
                if (!isNaN(lat) && !isNaN(lng)) {
                    map.flyTo([lat, lng], 7);
                }
            }
            return;
        }

        let totalLat = 0;
        let totalLng = 0;
        let count = 0;
        const coords: [number, number][] = [];

        realRegions.forEach((region) => {
            const detectors = detectorDataRegionalized[region] || [];
            if (detectors.length > 0) {
                const lat = Number(detectors[0].status.gps_lat);
                const lng = Number(detectors[0].status.gps_long);
                if (!isNaN(lat) && !isNaN(lng)) {
                    totalLat += lat;
                    totalLng += lng;
                    count++;
                    coords.push([lat, lng]);
                }
            }
        });

        if (count > 0) {
            const avgLat = totalLat / count;
            const avgLng = totalLng / count;

            const uniqueCountries = new Set(
                realRegions.map((r) => regionCountriesRef.current[r]).filter(Boolean)
            );

            let zoomLevel = 4;
            if (uniqueCountries.size > 1) {
                zoomLevel = 2;
            } else {
                let maxDistance = 0;
                for (let i = 0; i < coords.length; i++) {
                    for (let j = i + 1; j < coords.length; j++) {
                        const d = haversineDistance(
                            coords[i][0], coords[i][1],
                            coords[j][0], coords[j][1]
                        );
                        if (d > maxDistance) maxDistance = d;
                    }
                }
                if (maxDistance < 300) {
                    zoomLevel = 6;
                }
            }

            map.flyTo([avgLat, avgLng], zoomLevel);
        }
    }, [selectedRegions, detectorDataRegionalized]);


    // ----------------------------------------------- Chart data -----------------------------------------------

    interface RegionBarData {
        region: string;
        label: string;
        online: number;
        offline: number;
        total: number;
        percent: number;
    }

    const regionBarData = useMemo((): RegionBarData[] => {
        const regionNames = selectedRegions.includes("All")
            ? Object.keys(regionData)
            : selectedRegions.filter(r => r in regionData);

        const rows = regionNames.map(region => {
            const detectors = regionData[region] || [];
            const online = detectors.filter(d => d.online).length;
            const total = detectors.length;
            const percent = total > 0 ? Math.round((online / total) * 100) : 0;
            return { region, label: formatRegionName(region), online, offline: total - online, total, percent };
        });

        // Unassigned always first, then real regions sorted by health
        return [
            ...rows.filter(r => r.region === UNASSIGNED),
            ...rows.filter(r => r.region !== UNASSIGNED).sort((a, b) => b.percent - a.percent),
        ];
    }, [selectedRegions, regionData]);

    const statusSegments = [
        { key: "all" as const, label: "Total", count: selectedDetectors.length },
        { key: "online" as const, label: "Online", count: onlineCount },
        { key: "offline" as const, label: "Offline", count: offlineCount },
    ];


    return (
        <div className='main-hub-dash-and-map'>

            <div className={`main-hub-map${isFullscreen ? " mh-map-fullscreen" : ""}`}>
                <button
                    type="button"
                    className="mh-fullscreen-btn"
                    onClick={() => setIsFullscreen(f => !f)}
                    aria-pressed={isFullscreen}
                    aria-label={isFullscreen ? "Exit fullscreen map" : "Show map fullscreen"}
                    title={isFullscreen ? "Exit fullscreen (Esc)" : "Fullscreen"}
                >
                    {isFullscreen ? (
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                            <path d="M5 1H1v4M11 1h4v4M5 15H1v-4M11 15h4v-4M6 6l-5-5M10 6l5-5M6 10l-5 5M10 10l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                    ) : (
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                            <path d="M1 5V1h4M11 1h4v4M1 11v4h4M15 11v4h-4M1 1l5 5M15 1l-5 5M1 15l5-5M15 15l-5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                    )}
                </button>

                <MapContainer
                    center={[0.00, 0.00]}
                    zoom={2}
                    style={{ width: "100%", height: "100%" }}
                    ref={mapRef}>

                    <MapResizer trigger={isFullscreen} />

                    <TileLayer
                        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    />
                    {/* <SearchControl /> */}

                    {filteredDetectors.map((detector) => {
                        const lat = Number(detector.status.gps_lat);
                        const lng = Number(detector.status.gps_long);

                        if (!lat || !lng) return null;

                        return (
                            <Marker
                                key={detector.mac}
                                position={[lat, lng]}
                                icon={getColorForMarker(detector.online, Number(detector.status.memory))}
                                ref={el => (markerRefs.current[detector.mac] = el)}
                            >
                                <Popup>
                                    <img src={fnnPopupLogo} alt="" className="fnn-popup-team-dash" />
                                    <div className='new-popup-content'>
                                        <p>
                                            <strong>Detector:</strong>{" "}
                                            <span style={{ color: '#28aee1' }}>
                                                {formatDetectorName(detector.codeName, detector.status.site_name || detector.mac)}
                                            </span>
                                        </p>
                                        <p><strong>MAC:</strong> {detector.mac}</p>
                                        <p>
                                            <strong>Status:</strong>{" "}
                                            <span style={{ color: detector.online ? 'green' : 'red' }}>
                                                {detector.online ? 'Online' : 'Offline'}
                                            </span>
                                        </p>
                                        <p><strong>Latitude:</strong> {lat}</p>
                                        <p><strong>Longitude:</strong> {lng}</p>
                                        <div className='pop-changeableValues'>
                                            <strong> GPS Fix: </strong>
                                            <span style={{ color: getColorForGPSFix(detector.status.gps_fix) }}>
                                                {detector.status.gps_fix ? "Yes" : "No"}
                                            </span>
                                        </div>
                                        <div className='google-maps-button-div'
                                            onClick={() =>
                                                window.open(
                                                    `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`,
                                                    "_blank",
                                                    "noopener,noreferrer"
                                                )
                                            }
                                        >
                                            <img src={googleMapsLogo} alt='gmaps' />
                                            <p>Google Maps</p>
                                        </div>
                                    </div>
                                </Popup>
                            </Marker>
                        );
                    })}
                </MapContainer>
            </div>

            <div className='main-hub-dash'>

                <div className="mh-header">
                    <h2>Detector Information</h2>
                    <div className="mh-header-controls">
                        <button
                            type="button"
                            role="switch"
                            aria-checked={showUnassigned}
                            className={`mh-toggle ${showUnassigned ? "on" : ""}`}
                            onClick={toggleShowUnassigned}
                            title="Include detectors that aren't in any region"
                        >
                            <span className="mh-toggle-track" aria-hidden="true">
                                <span className="mh-toggle-thumb" />
                            </span>
                            <span className="mh-toggle-label">
                                Unassigned
                                <span className="mh-toggle-count">{unassignedDetectors.length}</span>
                            </span>
                        </button>

                        <FilterDropdown
                            sections={[
                                {
                                    key: "regions",
                                    title: "Region",
                                    options: regionFilterOptions,
                                    type: "checkbox",
                                    formatLabel: (option) => formatRegionName(option),
                                },
                            ]}
                            onChange={handleFilterChange}
                            resetValue={{ regions: [] }}
                            dropdownClassName="mh-filter-dropdown"
                        />
                    </div>
                </div>

                <div className="mh-status-strip" role="group" aria-label="Filter detectors by status">
                    {statusSegments.map(seg => (
                        <button
                            key={seg.key}
                            type="button"
                            className={`mh-status-seg mh-status-${seg.key} ${statusFilter === seg.key ? "active" : ""}`}
                            aria-pressed={statusFilter === seg.key}
                            onClick={() => setStatusFilter(seg.key)}
                        >
                            <span className="mh-status-count">{seg.count}</span>
                            <span className="mh-status-label">{seg.label}</span>
                        </button>
                    ))}
                </div>

                <div className='main-hub-sum-and-chart'>
                    <div className="main-hub-chart-wrapper">
                        <div className="mh-region-list">
                            <div aria-live="polite">
                                {assignMessage && (
                                    <p className={`mh-assign-msg ${assignMessage.type}`}>
                                        {assignMessage.text}
                                        <button
                                            type="button"
                                            className="mh-assign-link"
                                            onClick={() => setAssignMessage(null)}
                                        >
                                            Dismiss
                                        </button>
                                    </p>
                                )}
                            </div>

                            {regionBarData.length === 0 && (
                                <p className="mh-region-empty">No regions to show yet.</p>
                            )}

                            {regionBarData.map(({ region, label, online, total, percent }) => {
                                const isUnassigned = region === UNASSIGNED;
                                const expanded = expandedRegions[region] ?? false;

                                const visibleDetectors = expanded
                                    ? (regionData[region] || [])
                                        .filter(d => {
                                            if (statusFilter === "online") return d.online;
                                            if (statusFilter === "offline") return !d.online;
                                            return true;
                                        })
                                        .map(enrichDetector)
                                    : [];

                                return (
                                    <React.Fragment key={region}>
                                        {isUnassigned && (
                                            <p className="mh-region-divider">Detectors not added to any region</p>
                                        )}

                                        <div className={`mh-region ${expanded ? "expanded" : ""} ${isUnassigned ? "mh-region-unassigned" : ""}`}>
                                            <button
                                                type="button"
                                                className="mh-region-header"
                                                aria-expanded={expanded}
                                                onClick={() =>
                                                    setExpandedRegions(prev => ({ ...prev, [region]: !expanded }))
                                                }
                                            >
                                                <span className="mh-region-name" title={label}>{label}</span>

                                                <span className={`mh-region-bar ${total === 0 ? "is-empty" : ""}`} aria-hidden="true">
                                                    <span className="mh-region-bar-online" style={{ width: `${percent}%` }} />
                                                </span>

                                                <span className="mh-region-counts">
                                                    <strong>{online}</strong>/{total}
                                                </span>

                                                <span className="mh-region-pct">{percent}%</span>

                                                <FaChevronDown className="mh-region-chevron" aria-hidden="true" />
                                            </button>

                                            {expanded && (
                                                <div className="mh-region-body">
                                                    {isUnassigned && assignAccess.canAssign && (
                                                        <div className="mh-assign-bar">
                                                            {!assignMode ? (
                                                                <button
                                                                    type="button"
                                                                    className="mh-assign-start"
                                                                    onClick={() => {
                                                                        setAssignMode(true);
                                                                        setAssignMessage(null);
                                                                    }}
                                                                >
                                                                    Add detectors to a region
                                                                </button>
                                                            ) : (
                                                                <>
                                                                    <span className="mh-assign-count">
                                                                        {validSelectedMacs.length > 0
                                                                            ? `${validSelectedMacs.length} selected`
                                                                            : "Select detectors below"}
                                                                    </span>

                                                                    <button
                                                                        type="button"
                                                                        className="mh-assign-link"
                                                                        onClick={() =>
                                                                            setSelectedMacs(
                                                                                validSelectedMacs.length === visibleDetectors.length
                                                                                    ? []
                                                                                    : visibleDetectors.map(d => d.mac)
                                                                            )
                                                                        }
                                                                    >
                                                                        {validSelectedMacs.length === visibleDetectors.length && visibleDetectors.length > 0
                                                                            ? "Clear selection"
                                                                            : "Select all"}
                                                                    </button>

                                                                    <select
                                                                        className="mh-assign-select"
                                                                        aria-label="Region to add the selected detectors to"
                                                                        value={targetRegion}
                                                                        onChange={(e) => setTargetRegion(e.target.value)}
                                                                    >
                                                                        <option value="">Choose a region</option>
                                                                        {assignAccess.regions.map(r => (
                                                                            <option key={r} value={r}>{formatRegionName(r)}</option>
                                                                        ))}
                                                                    </select>

                                                                    <button
                                                                        type="button"
                                                                        className="mh-assign-confirm"
                                                                        disabled={!targetRegion || validSelectedMacs.length === 0 || assigning}
                                                                        onClick={handleAssign}
                                                                    >
                                                                        {assigning
                                                                            ? "Adding…"
                                                                            : targetRegion
                                                                                ? `Add to ${formatRegionName(targetRegion)}`
                                                                                : "Add to region"}
                                                                    </button>

                                                                    <button
                                                                        type="button"
                                                                        className="mh-assign-link"
                                                                        disabled={assigning}
                                                                        onClick={exitAssignMode}
                                                                    >
                                                                        Cancel
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    )}

                                                    {visibleDetectors.length > 0 ? (
                                                        <div className="mh-progress-detectors">
                                                            {visibleDetectors.map(det =>
                                                                renderDetectorButton(det, isUnassigned && assignMode)
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <p className="mh-region-empty">
                                                            No {statusFilter === "all" ? "" : `${statusFilter} `}detectors in {label}.
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {isUnassigned && regionBarData.length > 1 && (
                                            <div className="mh-region-separator" aria-hidden="true" />
                                        )}
                                    </React.Fragment>
                                );
                            })}

                        </div>
                    </div>
                </div>

            </div>

        </div>
    );
};


export default MainHub